import React, { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./PackageRequestPage.css";

function PackageRequestPage() {
  const location = useLocation();
  const formContainerRef = useRef(null);

  const packageDetails = useMemo(() => {
    const pageParams = new URLSearchParams(location.search);

    return {
      packageName: pageParams.get("packageName"),
      packageLocation: pageParams.get("packageLocation"),
    };
  }, [location.search]);

  const formUrl = useMemo(() => {
    const baseFormUrl =
      "https://frontal-firestorm-840.forms.space/form-en5hs2";

    const formParams = new URLSearchParams();

    if (packageDetails.packageName) {
      formParams.set(
        "prefill.packageName",
        packageDetails.packageName
      );
    }

    if (packageDetails.packageLocation) {
      formParams.set(
        "prefill.packageLocation",
        packageDetails.packageLocation
      );
    }

    const queryString = formParams.toString();

    return queryString
      ? `${baseFormUrl}?${queryString}`
      : baseFormUrl;
  }, [packageDetails]);

  useEffect(() => {
    const scriptId = "formspace-package-embed-script";
    const timers = [];

    let iframe = null;
    let userStartedScrolling = false;

    const previousScrollRestoration =
      "scrollRestoration" in window.history
        ? window.history.scrollRestoration
        : null;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollToTop = () => {
      if (userStartedScrolling) {
        return;
      }

      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const scheduleScrollReset = (delay) => {
      const timer = window.setTimeout(scrollToTop, delay);
      timers.push(timer);
    };

    const stopAutomaticScrollReset = () => {
      userStartedScrolling = true;

      timers.forEach((timer) => {
        window.clearTimeout(timer);
      });
    };

    // Keep the page at the top while Formspace initializes.
    [
      0,
      50,
      150,
      300,
      600,
      1000,
      1600,
      2400,
      3200,
    ].forEach(scheduleScrollReset);

    window.addEventListener(
      "wheel",
      stopAutomaticScrollReset,
      {
        once: true,
        passive: true,
      }
    );

    window.addEventListener(
      "touchstart",
      stopAutomaticScrollReset,
      {
        once: true,
        passive: true,
      }
    );

    window.addEventListener(
      "keydown",
      stopAutomaticScrollReset,
      {
        once: true,
      }
    );

    const handleIframeLoad = () => {
      [0, 100, 350, 750, 1400, 2200].forEach(
        scheduleScrollReset
      );
    };

    const observer = new MutationObserver(() => {
      const detectedIframe =
        formContainerRef.current?.querySelector("iframe");

      if (detectedIframe && detectedIframe !== iframe) {
        iframe = detectedIframe;

        iframe.addEventListener(
          "load",
          handleIframeLoad,
          {
            once: true,
          }
        );

        [0, 100, 400, 900].forEach(
          scheduleScrollReset
        );
      }
    });

    if (formContainerRef.current) {
      observer.observe(formContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    const existingScript =
      document.getElementById(scriptId);

    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");

    script.id = scriptId;
    script.src = "https://formspace.com/embed.js";
    script.defer = true;
    script.crossOrigin = "anonymous";

    script.addEventListener("load", () => {
      [0, 100, 400, 900, 1600].forEach(
        scheduleScrollReset
      );
    });

    document.body.appendChild(script);

    return () => {
      observer.disconnect();

      timers.forEach((timer) => {
        window.clearTimeout(timer);
      });

      window.removeEventListener(
        "wheel",
        stopAutomaticScrollReset
      );

      window.removeEventListener(
        "touchstart",
        stopAutomaticScrollReset
      );

      window.removeEventListener(
        "keydown",
        stopAutomaticScrollReset
      );

      if (iframe) {
        iframe.removeEventListener(
          "load",
          handleIframeLoad
        );
      }

      const scriptToRemove =
        document.getElementById(scriptId);

      if (scriptToRemove) {
        scriptToRemove.remove();
      }

      if (
        previousScrollRestoration &&
        "scrollRestoration" in window.history
      ) {
        window.history.scrollRestoration =
          previousScrollRestoration;
      }
    };
  }, [formUrl, location.pathname, location.search]);

  return (
    <main className="package-request-page">
      <section className="package-request-hero">
        <div className="package-request-intro">
          <p className="package-request-eyebrow">
            TEAMWARD PACKAGES
          </p>

          <h1>Customize your team experience.</h1>

          <p className="package-request-description">
            Tell us what you’d like to keep, change, or add.
            We’ll review your preferences and follow up with
            recommended options, availability, and estimated
            pricing.
          </p>

          {(packageDetails.packageName ||
            packageDetails.packageLocation) && (
            <div className="package-selected">
          

              <div className="package-selected-copy">
                <span>You’re customizing</span>

                {packageDetails.packageName && (
                  <strong>
                    {packageDetails.packageName}
                  </strong>
                )}

                {packageDetails.packageLocation && (
                  <p>
                    {packageDetails.packageLocation}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="package-request-content">
        <div className="package-request-content-inner">
          <div className="package-request-note">
            <div
  className="package-request-note-icon"
  aria-hidden="true"
>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4h6.2c.53 0 1.04.21 1.41.59l8.8 8.8a2 2 0 0 1 0 2.82l-4.2 4.2a2 2 0 0 1-2.82 0l-8.8-8.8A2 2 0 0 1 4 10.2V4Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="8"
      cy="8"
      r="1.25"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
</div>

            <div>
              <h2>No cost to submit a request</h2>

              <p>
                Package pricing and any optional planning
                fees will be clearly presented before you
                approve anything.
              </p>
            </div>
          </div>

          <div className="package-form-heading">
            <p className="package-form-eyebrow">
              YOUR REQUEST
            </p>

            <h2>Tell us what your team needs.</h2>

            <p>
              Complete the form below and we’ll take it from
              there.
            </p>
          </div>

          <section
            ref={formContainerRef}
            className="package-form-container"
          >
            <div
              key={formUrl}
              data-formspace={formUrl}
            />
          </section>
        </div>
      </section>
    </main>
  );
}

export default PackageRequestPage;
